import { ChangeEvent, forwardRef, useEffect, useImperativeHandle, useState } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faArrowLeft } from '@fortawesome/pro-light-svg-icons';
import { faCircle } from '@fortawesome/pro-light-svg-icons';
import { faCircleCheck, faMinus, faPlus } from '@fortawesome/pro-solid-svg-icons';
import { useDispatch, useSelector } from 'react-redux';
import useNonInitialEffect from '@versiondos/hooks';
import { Lot, Product } from '@spike/product-model';
import { ProductsStatus, saveMovementThunk } from '@spike/products-action';
import { showError, showSuccess } from '@spike/notifications-action';
import { RootState } from 'store';
import { useApiClientWrapper } from 'hooks';
import { Textarea } from 'components/UI/V3/Forms/Textarea/Textarea';
import { ProductHeader } from '../StockSidebar/ProductHeader';
import { QuantityStepper } from './QuantityStepper';
import { StockAdjustmentMode } from '../StockSidebar/AdjustStock';
import clsx from 'clsx';
import stock from '../StockSidebar/StockSidebar.module.scss';
import styles from './LotsSidebar.module.scss';

interface AdjustLotProps {
    product: Product;
    lot: Lot;
    onBack: () => void;
    onSaved: () => void;
    onStateChange?: (state: { canSave: boolean; saving: boolean }) => void;
}

export interface AdjustLotHandle {
    save: () => void;
    canSave: boolean;
    saving: boolean;
}

export const AdjustLot = forwardRef<AdjustLotHandle, AdjustLotProps>(
    ({ product, lot, onBack, onSaved, onStateChange }, ref) => {
        const dispatch = useDispatch();
        const apiClientWrapper = useApiClientWrapper();
        const status = useSelector<RootState, ProductsStatus>(state => state.products.status);
        const [mode, setMode] = useState<StockAdjustmentMode>('add');
        const [quantity, setQuantity] = useState(1);
        const [reason, setReason] = useState('');

        const businessProductId = product.businessProduct?.id;
        const saving = status === ProductsStatus.SavingMovement;
        const canSave =
            quantity > 0 && reason.trim().length > 0 && !saving && businessProductId != null && lot.id != null;

        useNonInitialEffect(() => {
            if (status === ProductsStatus.SaveMovementSuccess) {
                dispatch(showSuccess('Stock adjustment created successfully.'));
                onSaved();
            }
        }, [status]);

        useEffect(() => {
            onStateChange?.({ canSave, saving });
        }, [canSave, saving, onStateChange]);

        const handleSave = () => {
            if (!canSave || businessProductId == null || lot.id == null) {
                return;
            }

            const delta = mode === 'add' ? quantity : -quantity;

            if (mode === 'reduce' && (lot.quantity ?? 0) - quantity < 0) {
                dispatch(showError('Quantity cannot exceed available stock.'));
                return;
            }

            dispatch(saveMovementThunk(apiClientWrapper, businessProductId, delta, reason.trim(), lot.id));
        };

        useImperativeHandle(ref, () => ({
            save: handleSave,
            canSave,
            saving
        }));

        return (
            <>
                <div className={stock.detailsHeader}>
                    <button
                        type="button"
                        className={stock.backButton}
                        onClick={onBack}
                        aria-label="Back to lots"
                        disabled={saving}
                    >
                        <FontAwesomeIcon icon={faArrowLeft} />
                    </button>
                    <h2 className={stock.detailsTitle}>Adjust Lot</h2>
                </div>
                <ProductHeader
                    product={product}
                    trackingLabel="Track by Lots"
                />
                <div className={styles.adjustDivider} />
                <div className={styles.readonlyRow}>
                    <span className={styles.lotNumberLabel}>Lot Number</span>
                    <span className={styles.readonlyValue}>{lot.lotNumber || '-'}</span>
                </div>
                <div className={stock.modeRow}>
                    <button
                        type="button"
                        className={clsx(stock.modeButton, mode === 'add' && stock.modeButtonActive)}
                        onClick={() => setMode('add')}
                        disabled={saving}
                    >
                        <FontAwesomeIcon
                            icon={faPlus}
                            className={stock.modeIcon}
                        />
                        Add Stock
                        <FontAwesomeIcon
                            icon={mode === 'add' ? faCircleCheck : faCircle}
                            className={mode === 'add' ? stock.checkIcon : stock.radioIcon}
                        />
                    </button>
                    <button
                        type="button"
                        className={clsx(stock.modeButton, mode === 'reduce' && stock.modeButtonActive)}
                        onClick={() => setMode('reduce')}
                        disabled={saving}
                    >
                        <FontAwesomeIcon
                            icon={faMinus}
                            className={stock.modeIcon}
                        />
                        Reduce Stock
                        <FontAwesomeIcon
                            icon={mode === 'reduce' ? faCircleCheck : faCircle}
                            className={mode === 'reduce' ? stock.checkIcon : stock.radioIcon}
                        />
                    </button>
                </div>
                <div className={stock.quantityRow}>
                    <span className={stock.quantityLabel}>Quantity</span>
                    <QuantityStepper
                        quantity={quantity}
                        disabled={saving}
                        onChange={setQuantity}
                    />
                </div>
                <div className={clsx(stock.field, stock.reasonField)}>
                    <Textarea
                        id="lot-reason"
                        label="Reason"
                        placeholder="Enter the reason"
                        required
                        value={reason}
                        maxLength={100}
                        onChange={(event: ChangeEvent<HTMLTextAreaElement>) => setReason(event.target.value)}
                        disabled={saving}
                        rows={1}
                    />
                </div>
            </>
        );
    }
);

AdjustLot.displayName = 'AdjustLot';

export default AdjustLot;
